import React, { useState } from 'react';
import { Button, Snackbar, Alert, CircularProgress } from '@mui/material';
import { Print } from '@mui/icons-material';
import { CustomWidthTooltipNoImage } from '../utils/customWidthToolTipUtilNoImage';

const PrintTableButton = ({
  tableData = [],
  columns = [],
  title = 'Alerts Report',
  buttonText = 'Print',
  includeHeader = true,
  includeFooter = true,
  customStyles = {},
  className = '',
  style = {},
  logo = 'images/icons/ccg.jpg'
}) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success'
  });

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 6000);
  };

  const printTable = async () => {
    setIsPrinting(true);
    try {
      if (!tableData || tableData.length === 0) {
        throw new Error('No data available to print');
      }

      let headers = columns.length > 0 ? columns : [
        { display: 'Sensor', field: 'sensor' },
        { display: 'Events', field: 'events' },
        { display: 'Severity', field: 'severity' },
        { display: 'First Seen', field: 'first_seen' },
        { display: 'Last Seen', field: 'last_seen' },
        { display: 'Source', field: 'source' },
        { display: 'ID', field: 'id' },
        { display: 'Alert Message', field: 'message' },
        { display: 'Phen.AI Ignore', field: 'phen_ignore' },
        { display: 'Reference', field: 'reference' }
      ];

      const currentDate = new Date().toLocaleDateString();
      const currentTime = new Date().toLocaleTimeString();

      const printWindow = window.open('.', '_blank');
      const doc = printWindow.document;

      doc.open();
      doc.write('<!DOCTYPE html><html><head><meta charset="utf-8"><title>Print Report</title></head><body></body></html>');
      doc.close();

      const styleElement = doc.createElement('style');
      styleElement.textContent = `
        @media print {
          @page { margin: 0.5in; size: landscape; }
        }
        .logo {
          float: left;
        }
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          font-size: 12px;
          color: #2a2a2a;
          margin: 0;
          padding: 20px;
          background: #ffffff;
        }
        .print-header {
          text-align: center;
          margin-bottom: 24px;
          border-bottom: 3px solid #333333;
          padding-bottom: 16px;
        }
        .print-title {
          font-size: 28px;
          font-weight: 700;
          color: #2a2a2a;
          margin-bottom: 8px;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          justify-content: center;
        }
        .print-date {
          font-size: 12px;
          color: #666666;
          font-weight: 400;
          letter-spacing: 0.01em;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          border: 2px solid #333333;
        }
        th, td {
          border: 1px solid #cccccc;
          padding: 10px 8px;
          text-align: left;
        }
        th {
          background: linear-gradient(135deg, #2a2a2a 0%, #555555 100%);
          color: white;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        tr:nth-child(even) { 
          background-color: #f8f8f8; 
        }
        tr:nth-child(odd) { 
          background-color: #ffffff; 
        }
        tbody tr:hover {
          background-color: #f0f0f0;
        }
        .severity-high { 
          background: #666666; 
          color: white; 
          padding: 3px 10px; 
          border-radius: 12px; 
          font-size: 10px; 
          font-weight: 600; 
          text-transform: uppercase;
        }
        .severity-medium { 
          background: #888888; 
          color: white; 
          padding: 3px 10px; 
          border-radius: 12px; 
          font-size: 10px; 
          font-weight: 600; 
          text-transform: uppercase;
        }
        .severity-low { 
          background: #aaaaaa; 
          color: white; 
          padding: 3px 10px; 
          border-radius: 12px; 
          font-size: 10px; 
          font-weight: 600; 
          text-transform: uppercase;
        }
        .id-chip { 
          background: #333333; 
          color: white; 
          padding: 3px 10px; 
          border-radius: 12px; 
          font-size: 10px; 
          font-weight: 600; 
        }
        .print-footer {
          text-align: center;
          font-size: 11px;
          color: #666666;
          border-top: 2px solid #cccccc;
          padding-top: 16px;
          margin-top: 24px;
          font-weight: 500;
          letter-spacing: 0.01em;
        }
        ${Object.entries(customStyles).map(([selector, styles]) =>
          `${selector} { ${Object.entries(styles).map(([k, v]) =>
            `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${v};`
          ).join(' ')} }`).join('\n')}
      `;

      doc.head.appendChild(styleElement);

      const container = doc.body;

      if (includeHeader) {
        const headerDiv = doc.createElement('div');
        headerDiv.className = 'print-header';
        headerDiv.innerHTML = `
          <img class="logo" src=${logo} alt="ccg" width="80px" height="40px" > 
          <div class="print-title">${title}</div>
          <div class="print-date">Generated on ${currentDate} at ${currentTime}</div>
        `;
        container.appendChild(headerDiv);
      }

      const table = doc.createElement('table');
      const thead = doc.createElement('thead');
      const headerRow = doc.createElement('tr');
      headers.forEach(h => {
        const th = doc.createElement('th');
        th.textContent = h.display || h.field || h;
        headerRow.appendChild(th);
      });
      thead.appendChild(headerRow);
      table.appendChild(thead);

      const tbody = doc.createElement('tbody');
      tableData.forEach(row => {
        const tr = doc.createElement('tr');
        headers.forEach(h => {
          const field = h.field || h;
          const value = row[field];
          const td = doc.createElement('td');

          if (field === 'severity') {
            const span = doc.createElement('span');
            span.className = `severity-${value}`;
            span.textContent = value?.toUpperCase() || '-';
            td.appendChild(span);
          } else if (field === 'id') {
            const span = doc.createElement('span');
            span.className = 'id-chip';
            span.textContent = value || '-';
            td.appendChild(span);
          } else if (field === 'events') {
            td.textContent = value ? Number(value).toLocaleString() : '-';
          } else if (field === 'phen_ignore') {
            td.textContent = value ? 'Yes' : 'No';
          } else if (field === 'message') {
            const message = String(value || '');
            td.textContent = message.length > 80 ? message.slice(0, 80) + '...' : message;
          } else if (field === 'sparkline') {
            td.textContent = 'Chart Data';
          } else if (value === null || value === undefined) {
            td.textContent = '-';
          } else if (typeof value === 'boolean') {
            td.textContent = value ? 'Yes' : 'No';
          } else if (typeof value === 'number') {
            td.textContent = value.toLocaleString();
          } else {
            td.textContent = String(value);
          }

          tr.appendChild(td);
        });
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);
      container.appendChild(table);

      if (includeFooter) {
        const footer = doc.createElement('div');
        footer.className = 'print-footer';
        footer.textContent = `Total Records: ${tableData.length} | Page 1 of 1`;
        footer.innerHTML = `
        <div class="print-date">Total Records: ${tableData.length} | Page 1 of 1 </div>
        <div class="print-date">Copyright © 2015-2026, CCG, All rights reserved.</div>
        `;
        container.appendChild(footer);
      }

      printWindow.focus();
      printWindow.print();

    } catch (error) {
      console.error('Print error:', error);
      showNotification(`Print failed: ${error.message}`, 'error');
    } finally {
      setIsPrinting(false);
    }
  };

  //const toolTip = "Show alerts generated in the time selected.";

  return (
    <CustomWidthTooltipNoImage placement="top" dynamicWidth="250px" dyanmicHeight="25px" >
      <Button
        variant="contained"
        onClick={printTable}
        disabled={isPrinting}
        startIcon={isPrinting ? <CircularProgress size={16} color="inherit" /> : <Print />}
        className={className}
        sx={{
          backgroundColor: '#2a2a2a',
          color: '#ffffff',
          height: '36px',
          textTransform: 'none',
          fontWeight: 500,
          letterSpacing: '0.02em',
          borderRadius: '6px',
          fontSize: '0.875rem',
          px: 2,
          minWidth: 'auto',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          border: '1px solid #555555',
          '&:hover': {
            backgroundColor: '#404040',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            transform: 'translateY(-1px)',
          },
          '&:disabled': {
            backgroundColor: '#1a1a1a',
            color: '#888888',
            boxShadow: 'none',
            transform: 'none',
            border: '1px solid #333333',
          },
          ...style
        }}
      >
        {isPrinting ? 'Printing...' : buttonText}
      </Button>

      <Snackbar
        open={notification.show}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, show: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setNotification(prev => ({ ...prev, show: false }))}
          severity={notification.type === 'error' ? 'error' : 'success'}
          sx={{ 
            width: '100%',
            backgroundColor: notification.type === 'error' ? '#666666' : '#888888',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            },
            '& .MuiAlert-action': {
              color: 'white'
            }
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </CustomWidthTooltipNoImage>
  );
};

export default PrintTableButton;